import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  "data-test": "explainer",
  class: "text-sm leading-relaxed text-gray-600"
}
const _hoisted_2 = {
  key: 0,
  class: "text-sm text-red-600"
}
const _hoisted_3 = {
  key: 1,
  class: "space-y-3"
}
const _hoisted_4 = {
  key: 0,
  "data-test": "plan-changed",
  class: "text-sm text-amber-700"
}
const _hoisted_5 = { class: "-mx-6 overflow-x-auto px-6" }
const _hoisted_6 = { class: "min-w-[51rem] text-sm" }
const _hoisted_7 = ["data-test"]
const _hoisted_8 = {
  "data-test": "cell-prices",
  class: "text-right"
}
const _hoisted_9 = {
  "data-test": "cell-blockers",
  class: "text-right"
}
const _hoisted_10 = {
  "data-test": "cell-name",
  class: "truncate font-medium text-gray-900"
}
const _hoisted_11 = ["data-test"]
const _hoisted_12 = {
  "data-test": "cell-prices",
  class: "text-right"
}
const _hoisted_13 = {
  key: 0,
  class: "mt-1 space-y-0.5 text-xs text-amber-700"
}
const _hoisted_14 = {
  key: 2,
  class: "mt-3 text-sm text-red-600"
}

import { computed, ref, watch } from 'vue'

import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TheModal from '@/components/TheModal.vue'
import api from '@/store/api'

const TITLE = 'Turn on menu propagation?'
const EXPLAINER =
  'Menu propagation makes the franchise menu the source of truth. Every product, category, menu placement and tier price on it is created or updated in every location. Items in a location that match the franchise menu are overwritten and locked; locations keep their own extra products. Fulfillment, questions and membership tiers are untouched. Turning it off later lifts the locks but does not undo the changes.'

export default {
  __name: 'FranchiseMenuEnableModal',
  props: {
  open: { type: Boolean, default: false }
},
  emits: ['close', 'enabled'],
  setup(__props, { emit: __emit }) {

const SPLIT_KEYS = ['stores', 'products', 'categories', 'placements']
const COLUMNS = [{ width: 'minmax(9rem, 2fr)' }, ...Array(6).fill({ width: 'minmax(6rem, 1fr)' })]
const NO_CACHE = { receive: false }

const props = __props
// enabled hands over the status payload from franchise/menu/enable.
const emit = __emit

const step = ref('explain')
const plan = ref(null)
const loading = ref(false)
const loadFailed = ref(false)
const planChanged = ref(false)
const enabling = ref(false)
const enableError = ref('')
let latestPreview = 0

const confirmLabel = computed(() => {
  const count = plan.value?.locations.length
  if (count === undefined) return 'Turn on and sync'
  return `Turn on and sync ${count} ${count === 1 ? 'location' : 'locations'}`
})

const splitText = counts => `${counts.matched} / ${counts.create}`

const reset = () => {
  latestPreview += 1
  step.value = 'explain'
  plan.value = null
  loading.value = false
  loadFailed.value = false
  planChanged.value = false
  enabling.value = false
  enableError.value = ''
}

const showPreview = async () => {
  const token = ++latestPreview
  step.value = 'preview'
  plan.value = null
  loadFailed.value = false
  planChanged.value = false
  enableError.value = ''
  loading.value = true
  try {
    const response = await api.call('POST', 'franchise/menu/preview', {}, NO_CACHE)
    if (token === latestPreview) plan.value = response.data
  } catch {
    if (token === latestPreview) loadFailed.value = true
  } finally {
    if (token === latestPreview) loading.value = false
  }
}

const errorMessage = error => error?.response?.data?.error || 'Could not turn on menu propagation. Try again.'

const enable = async () => {
  enabling.value = true
  enableError.value = ''
  try {
    const response = await api.call('POST', 'franchise/menu/enable', { fingerprint: plan.value.fingerprint }, NO_CACHE)
    emit('enabled', response.data)
  } catch (error) {
    // The menu changed after the preview was drawn: show the plan the server would now run, to confirm again.
    if (error?.response?.status === 409 && error.response.data?.plan) {
      plan.value = error.response.data.plan
      planChanged.value = true
    } else {
      enableError.value = errorMessage(error)
    }
  } finally {
    enabling.value = false
  }
}

watch(
  () => props.open,
  open => {
    if (open) reset()
  }
)

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(TheModal, {
    "is-open": __props.open,
    "modal-title": TITLE,
    "max-width": "max-w-4xl",
    onCloseModal: _cache[2] || (_cache[2] = $event => (emit('close')))
  }, {
    main: _withCtx(() => [
      (step.value === 'explain')
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(EXPLAINER)))
        : (_openBlock(), _createBlock(NeueLoading, {
            key: 1,
            loading: loading.value
          }, {
            default: _withCtx(() => [
              (loadFailed.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_2, "Could not load the preview. Try again."))
                : (plan.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      (planChanged.value)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_4, " The franchise menu changed. Review the updated numbers. "))
                        : _createCommentVNode("", true),
                      _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs text-gray-500" }, " Counts read matched / create: what each location already has, and what the sync will add. ", -1)),
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                          _createVNode(TablistGrid, {
                            "data-test": "plan-header",
                            columns: COLUMNS,
                            header: "",
                            class: "border-b border-gray-200 pb-2"
                          }, {
                            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                              _createElementVNode("div", null, "Location", -1),
                              _createElementVNode("div", null, "Stores", -1),
                              _createElementVNode("div", null, "Products", -1),
                              _createElementVNode("div", null, "Categories", -1),
                              _createElementVNode("div", null, "Placements", -1),
                              _createElementVNode("div", { class: "text-right whitespace-normal" }, "Prices to set", -1),
                              _createElementVNode("div", { class: "text-right" }, "Blockers", -1)
                            ]))]),
                            _: 1
                          }),
                          _createVNode(TablistGrid, {
                            "data-test": "plan-total",
                            columns: COLUMNS,
                            class: "border-b border-gray-200 py-2 font-medium text-gray-900"
                          }, {
                            default: _withCtx(() => [
                              _cache[4] || (_cache[4] = _createElementVNode("div", { "data-test": "cell-name" }, "All locations", -1)),
                              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SPLIT_KEYS, (key) => {
                                return _createElementVNode("div", {
                                  key: key,
                                  "data-test": `cell-${key}`
                                }, _toDisplayString(splitText(plan.value.totals[key])), 9, _hoisted_7)
                              }), 64)),
                              _createElementVNode("div", _hoisted_8, _toDisplayString(plan.value.totals.prices.set), 1),
                              _createElementVNode("div", _hoisted_9, _toDisplayString(plan.value.totals.blockers), 1)
                            ]),
                            _: 1
                          }),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(plan.value.locations, (location) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: location.merchant_id,
                              "data-test": "plan-row",
                              class: "border-b border-gray-100 py-2 text-gray-600 last:border-b-0"
                            }, [
                              _createVNode(TablistGrid, { columns: COLUMNS }, {
                                default: _withCtx(() => [
                                  _createElementVNode("div", _hoisted_10, _toDisplayString(location.name), 1),
                                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SPLIT_KEYS, (key) => {
                                    return _createElementVNode("div", {
                                      key: key,
                                      "data-test": `cell-${key}`
                                    }, _toDisplayString(splitText(location[key])), 9, _hoisted_11)
                                  }), 64)),
                                  _createElementVNode("div", _hoisted_12, _toDisplayString(location.prices.set), 1),
                                  _createElementVNode("div", {
                                    "data-test": "cell-blockers",
                                    class: _normalizeClass(["text-right", location.blockers.length && 'font-medium text-amber-700'])
                                  }, _toDisplayString(location.blockers.length), 3)
                                ]),
                                _: 2
                              }, 1024),
                              (location.blockers.length)
                                ? (_openBlock(), _createElementBlock("ul", _hoisted_13, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(location.blockers, (message) => {
                                      return (_openBlock(), _createElementBlock("li", { key: message }, _toDisplayString(message), 1))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          }), 128))
                        ])
                      ])
                    ]))
                  : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["loading"])),
      (enableError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(enableError.value), 1))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      (step.value === 'explain')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(NeueButton, {
              type: "secondary",
              onClick: _cache[0] || (_cache[0] = $event => (emit('close')))
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("Cancel", -1)
              ]))]),
              _: 1
            }),
            _createVNode(NeueButton, { onClick: showPreview }, {
              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode("Continue", -1)
              ]))]),
              _: 1
            })
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(NeueButton, {
              type: "secondary",
              onClick: _cache[1] || (_cache[1] = $event => (step.value = 'explain'))
            }, {
              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                _createTextVNode("Back", -1)
              ]))]),
              _: 1
            }),
            _createVNode(NeueButton, {
              disabled: !plan.value || loading.value,
              loading: enabling.value,
              onClick: enable
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(confirmLabel.value), 1)
              ]),
              _: 1
            }, 8, ["disabled", "loading"])
          ], 64))
    ]),
    _: 1
  }, 8, ["is-open"]))
}
}

}